import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Trip } from '../../types/trip';
import { QualificationBadge } from '../ui/Badge';
import { formatDate, formatTime, formatDistance } from '../../utils/formatters';

interface TripTableProps {
  trips: Trip[];
  isLoading: boolean;
  onRefresh?: () => void;
  onView?: (trip: Trip) => void;
}

const columnHelper = createColumnHelper<Trip>();

const getDriverName = (trip: Trip): string => {
  if (typeof trip.driver_id === 'object' && trip.driver_id) {
    return trip.driver_id.name;
  }
  return trip.driver_name || 'Unknown';
};

const getVehiclePlate = (trip: Trip): string => {
  if (typeof trip.vehicle_id === 'object' && trip.vehicle_id) {
    return trip.vehicle_id.plate_number;
  }
  return trip.vehicle_plate || 'Unknown';
};

export const TripTable: React.FC<TripTableProps> = ({ trips, isLoading, onRefresh, onView }) => {
  const { t } = useTranslation();

  const columns: ColumnDef<Trip, any>[] = [
    columnHelper.accessor('start_time', {
      header: t('trips.table.dateTime'),
      cell: (info) => (
        <div>
          <div className="font-medium text-gray-900">{formatDate(info.getValue())}</div>
          <div className="text-sm text-gray-500">{formatTime(info.getValue())}</div>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'driver',
      header: t('trips.table.driver'),
      cell: ({ row }) => (
        <div className="text-sm text-gray-900">{getDriverName(row.original)}</div>
      ),
    }),
    columnHelper.display({
      id: 'vehicle',
      header: t('trips.table.vehicle'),
      cell: ({ row }) => (
        <div className="text-sm text-gray-900">{getVehiclePlate(row.original)}</div>
      ),
    }),
    columnHelper.display({
      id: 'distance',
      header: t('trips.table.distance'),
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-gray-900">{formatDistance(row.original.distance_km)}</span>
          {row.original.distance_source === 'gps' && (
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
              title={t('trips.table.distanceSourceGps')}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              GPS
            </span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('duration_minutes', {
      header: t('trips.table.duration'),
      cell: (info) => (
        <div className="text-sm text-gray-900">
          {info.getValue()} {t('trips.table.min')}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'qualification',
      header: t('trips.table.type'),
      cell: ({ row }) => {
        const trip = row.original;
        const warningTooltip = (() => {
          if (!trip.data_quality_flag) return null;
          const details = trip.data_quality_details ?? {};
          if (trip.data_quality_reason === 'impossible_speed') {
            const avg = details.avg_speed_kmh != null ? `${Math.round(details.avg_speed_kmh)} km/h` : null;
            return avg
              ? `Vitesse impossible détectée (${avg} moy.)`
              : 'Vitesse impossible détectée';
          }
          if (trip.data_quality_reason === 'odometer_gps_mismatch') {
            const odo = details.odo_distance_km != null ? `${details.odo_distance_km.toFixed(1)} km` : null;
            const gps = details.gps_distance_km != null ? `${details.gps_distance_km.toFixed(1)} km` : null;
            return odo && gps
              ? `Écart odomètre/GPS détecté (${odo} vs ${gps})`
              : 'Écart odomètre/GPS détecté';
          }
          return 'Données de trajet suspectes';
        })();

        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            <QualificationBadge type={trip.qualification} />
            {trip.source === 'smartcar' && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700"
                title="Trip imported from Smartcar"
              >
                SC
              </span>
            )}
            {trip.data_quality_flag && warningTooltip && (
              <span
                title={warningTooltip}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-600 cursor-default"
                aria-label={warningTooltip}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'odometer',
      header: t('trips.table.odometer'),
      cell: ({ row }) => {
        const startOdo = row.original.start_odometer || row.original.obd_data?.start_odometer;
        const endOdo = row.original.end_odometer || row.original.obd_data?.end_odometer;

        if (startOdo && endOdo) {
          return (
            <div className="text-sm text-gray-900">
              {startOdo.toLocaleString()} - {endOdo.toLocaleString()}
            </div>
          );
        }
        return <div className="text-sm text-gray-400">—</div>;
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <button
          onClick={() => onView?.(row.original)}
          className="text-primary-600 hover:text-primary-800 text-sm font-medium"
        >
          {t('trips.table.view')}
        </button>
      ),
    }),
  ];

  const table = useReactTable({
    data: trips,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className="animate-pulse">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-200 rounded mb-2" />
        ))}
      </div>
    );
  }

  if (trips.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">📊</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('trips.table.noTrips')}</h3>
        <p className="text-gray-500">{t('trips.table.noTripsMessage')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50 transition-colors">
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
